import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const CartContext = createContext(null);
const STORAGE_KEY = "bb_cart_v1";
const CART_ID_KEY = "bb_cart_id_v1";
const ATTR_KEY = "bb_attribution_v1";
const BOGO_OFFERED_KEY = "bb_bogo_offered_v1"; // sessionStorage — offer once per session

function makeCartId() {
  return "cart_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
}

function loadAttribution() {
  try { return JSON.parse(localStorage.getItem(ATTR_KEY) || "null"); } catch { return null; }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); } catch { return []; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pendingUpsell, setPendingUpsell] = useState(null); // { product, qty } while modal is showing

  const cartId = useMemo(() => {
    let cid = localStorage.getItem(CART_ID_KEY);
    if (!cid) {
      cid = makeCartId();
      localStorage.setItem(CART_ID_KEY, cid);
    }
    return cid;
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const track = useCallback((product_id, quantity, action) => {
    api.post("/cart/event", {
      cart_id: cartId, product_id, quantity, action,
      attribution: loadAttribution() || undefined,
    }, { withCredentials: true }).catch(() => {});
  }, [cartId]);

  // Actually push a product into the cart + track + open drawer.
  const performAdd = useCallback((product, qty) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      return existing
        ? prev.map((i) => i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i)
        : [...prev, { product_id: product.id, name: product.name, price: product.price, image: product.image_url || product.image, quantity: qty }];
    });
    track(product.id, qty, "add");
    setDrawerOpen(true);
  }, [track]);

  const addItem = useCallback((product, qty = 1) => {
    // Show BOGO upsell on the very first add-to-cart this session.
    const alreadyOffered = sessionStorage.getItem(BOGO_OFFERED_KEY) === "1";
    if (!alreadyOffered) {
      setPendingUpsell({ product, qty });
      return;
    }
    performAdd(product, qty);
  }, [performAdd]);

  // Called by BogoUpsell modal. `accepted=true` bumps qty by +2 so the customer
  // ends up with 3 units of the product (Buy 2, Get 1 Free).
  const resolveUpsell = useCallback((accepted) => {
    const pending = pendingUpsell;
    setPendingUpsell(null);
    sessionStorage.setItem(BOGO_OFFERED_KEY, "1");
    if (!pending) return { accepted: false, product: null };
    const finalQty = accepted ? pending.qty + 2 : pending.qty;
    performAdd(pending.product, finalQty);
    return { accepted, product: pending.product };
  }, [pendingUpsell, performAdd]);

  const updateQty = useCallback((product_id, quantity) => {
    setItems((prev) => {
      if (quantity <= 0) {
        track(product_id, 0, "remove");
        return prev.filter((i) => i.product_id !== product_id);
      }
      track(product_id, quantity, "update");
      return prev.map((i) => i.product_id === product_id ? { ...i, quantity } : i);
    });
  }, [track]);

  const removeItem = useCallback((product_id) => {
    track(product_id, 0, "remove");
    setItems((prev) => prev.filter((i) => i.product_id !== product_id));
  }, [track]);

  const clear = useCallback(() => setItems([]), []);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{
      items, addItem, updateQty, removeItem, clear,
      subtotal, count, drawerOpen, setDrawerOpen, cartId,
      pendingUpsell, resolveUpsell,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
